import { useConnection } from "wagmi";
import { Link } from "../components/Link";
import { Button } from "../components/ui";
import { Avatar, FormGuide, useTickFlash } from "../components/game";
import { ConnectButton } from "../wallet/ConnectButton";
import { useStreamStatus } from "../state/account";
import { streakOf, usePlays } from "../state/record";
import { openFunds } from "../state/sheets";
import { useValuations } from "../state/valuation";
import { big, usd } from "../lib/num";
import type { Route } from "../router";

export const NAV: { view: Route["view"]; label: string; href: string }[] = [
  { view: "play", label: "Play", href: "/play/BTC" },
  { view: "bankroll", label: "Bankroll", href: "/bankroll" },
];

/** The last five calls and the run they make. */
function Form() {
  const plays = usePlays();
  if (plays.length === 0) return null;
  const streak = streakOf(plays);
  return (
    <Link href="/bankroll" className="topbar-form" title="Your last five calls">
      <FormGuide plays={plays} count={5} size="sm" />
      {streak >= 2 && <span className={`topbar-streak ${streak >= 3 ? "is-hot" : ""}`}>{streak} in a row</span>}
    </Link>
  );
}

/** The bankroll, always in sight: tap it for the money page. */
function BankrollPill({ active }: { active: boolean }) {
  const { address } = useConnection();
  const stream = useStreamStatus();
  const valued = useValuations();
  const equity = valued ? big(valued.equity) : null;
  const flash = useTickFlash<HTMLSpanElement>(equity);
  if (!address) return null;
  return (
    <Link href="/bankroll" className="account-btn" aria-current={active ? "page" : undefined} aria-label="Your bankroll">
      <span className="account-words">
        <span className="account-label">Bankroll</span>
        <span ref={flash} className="account-equity">
          {equity !== null ? usd(equity) : stream === "live" ? "—" : <span className="muted">Loading</span>}
        </span>
      </span>
      <Avatar address={address.toLowerCase()} size={28} />
    </Link>
  );
}

export function TopBar({ route }: { route: Route }) {
  const { status, address } = useConnection();
  const connected = status === "connected" && address !== undefined;
  return (
    <header className="topbar">
      <div className="topbar-inner">
        <Link className="wordmark" href="/play/BTC" aria-label="playertrade, play">
          <span className="brand-mark" aria-hidden />
          playertrade
        </Link>
        <nav className="nav" aria-label="Primary">
          {NAV.map((item) => (
            <Link key={item.view} href={item.href} aria-current={route.view === item.view ? "page" : undefined}>
              {item.label}
            </Link>
          ))}
        </nav>
        <span className="topbar-spacer" />
        {connected ? (
          <>
            <Form />
            <Button tone="secondary" size="sm" className="topbar-deposit" onClick={() => openFunds("deposit")}>
              Deposit
            </Button>
            <BankrollPill active={route.view === "bankroll"} />
          </>
        ) : (
          <ConnectButton size="sm" />
        )}
      </div>
    </header>
  );
}
